import { useCallback } from 'react';

import { Sparkles } from 'lucide-react';

import {
  type ContextSelectionItem,
  ContextSelectionType,
  useContextSelection,
} from '@/components/chat/ContextSelectionContext';
import { ChatContextMenuItem } from '@/components/chat/chat-context-menu/ChatContextMenuItem';
import { ChatContextMenuItemSkeleton } from '@/components/chat/chat-context-menu/ChatContextMenuItemSkeleton';
import { ChatContextMenuSeparator } from '@/components/chat/chat-context-menu/ChatContextMenuSeparator';
import type { ToolItem } from '@/components/chat/chat-context-menu/hooks/useTools';
import {
  type SuggestionItem,
  useSuggestions,
} from '@/components/chat/chat-context-menu/tabs/hooks/useSuggestions';
import { ToolVersionSelector } from '@/components/chat/chat-context-menu/tools/ToolVersionSelector';
import { useToolVersionPickerFlow } from '@/components/chat/chat-context-menu/useToolVersionPickerFlow';

interface ChatContextMenuAllSuggestedProps {
  tools: ToolItem[];
}

export const ChatContextMenuAllSuggested = ({
  tools,
}: ChatContextMenuAllSuggestedProps) => {
  const { suggestions, loading } = useSuggestions();
  const { addContextSelection, runOnSelectItem } = useContextSelection();

  const handleCommit = useCallback(
    (selection: ContextSelectionItem) => {
      addContextSelection(selection);
      runOnSelectItem(selection);
    },
    [addContextSelection, runOnSelectItem]
  );

  const {
    pendingToolItem,
    isSheetOpen,
    requestToolSelection,
    confirmVersionSelection,
    handleSheetOpenChange,
    closeSheet,
  } = useToolVersionPickerFlow({ onCommit: handleCommit });

  const resolveToolItemForSuggestion = useCallback(
    (suggestionId: string) =>
      tools.find((tool) =>
        tool.versions.some((version) => version.id === suggestionId)
      ),
    [tools]
  );

  const handleClick = ({
    id,
    title,
    toolType,
    suggestionType,
    updatedAt,
  }: {
    id: string;
    title: string;
    toolType: SuggestionItem['toolType'];
    suggestionType: SuggestionItem['type'];
    updatedAt: string | null;
  }) => {
    if (suggestionType !== ContextSelectionType.Tool) {
      const nextSelection: ContextSelectionItem = {
        id: id,
        title: title,
        type: suggestionType,
        lastEditLabel: updatedAt ?? '',
      };

      handleCommit(nextSelection);

      return;
    }

    const matchedTool = resolveToolItemForSuggestion(id);

    if (matchedTool) {
      requestToolSelection(matchedTool, ContextSelectionType.Tool);

      return;
    }

    const fallbackSelection: ContextSelectionItem = {
      id: id,
      title: toolType,
      type: ContextSelectionType.Tool,
      lastEditLabel: updatedAt ?? '',
      selectedVersions: [id],
    };

    handleCommit(fallbackSelection);
  };

  return (
    <>
      <div className="flex items-center gap-2 px-2 py-1.5 text-sm font-semibold text-foreground">
        <Sparkles className="h-4 w-4 text-purple-600" />
        Suggested
      </div>
      <ChatContextMenuSeparator />
      {loading &&
        [1, 2].map((item) => <ChatContextMenuItemSkeleton key={item} />)}
      {!loading &&
        suggestions?.map((suggestion: SuggestionItem) => (
          <ChatContextMenuItem
            key={suggestion.id}
            onClick={() =>
              handleClick({
                id: suggestion.id,
                title: suggestion.title,
                suggestionType: suggestion.type,
                toolType: suggestion.toolType,
                updatedAt: suggestion.updatedAt,
              })
            }
          >
            {suggestion.type === ContextSelectionType.Tool
              ? `${suggestion.toolType}: ${suggestion.title}`
              : suggestion.title}
          </ChatContextMenuItem>
        ))}
      {pendingToolItem && (
        <ToolVersionSelector
          open={isSheetOpen}
          onOpenChange={handleSheetOpenChange}
          onClose={closeSheet}
          onConfirm={confirmVersionSelection}
          versions={pendingToolItem.versions.map((version) => ({
            id: version.id,
            name: version.name,
            editedAt: version.editedAt ?? '',
          }))}
          toolName={pendingToolItem.name}
        />
      )}
    </>
  );
};
